<template>
  <AnalysisPage pageName="progress" title="目標達成状況" :disabled="busy || !chart" :button="!!table">
    <AnalysisContent :noSet="isMounted && !table">
      <template #graph>
        <AnalysisAggregate :busy="busy" :refetch="fetch" :defaultUnit="2" />
        <LoadingIndicator v-if="busy" />
        <template v-else>
          <div class="progress-info">
            <span class="achievement-text">目標達成率</span>
            <ul>
              <li class="achievement">
                {{ achievementRate.integer }}<span class="decimal">.{{ achievementRate.decimal }}</span
                ><span class="rate">%</span>
              </li>
              <li class="diff">
                <span class="operator">{{ toOperator(diffType) }}</span>
                <span class="value">{{ numberFormat(Math.abs(diff)) }}</span
                >人
              </li>
              <li class="breakdown">
                <p>
                  目標<span class="value">{{ numberFormat(target) }}</span
                  ><span class="suffix">人</span>
                </p>
                <p>
                  実績<span class="value">{{ numberFormat(achievement) }}</span
                  ><span class="suffix">人</span>
                </p>
                <span class="visited">来店済み{{ numberFormat(visited) }}人</span>
              </li>
              <li class="compare">
                <p>
                  前月 <FluctuationIcon :type="mom.fluctuationType" /><span class="value">
                    <span class="operator">{{ toOperator(mom.fluctuationType) }}</span
                    >{{ mom.fluctuationValue }}</span
                  >%
                </p>
                <p>
                  前年 <FluctuationIcon :type="yoy.fluctuationType" /><span class="value"
                    ><span class="operator">{{ toOperator(yoy.fluctuationType) }}</span
                    >{{ yoy.fluctuationValue }}</span
                  >%
                </p>
              </li>
            </ul>
          </div>
          <hr />
          <FrameTemplate v-if="table" :busy="busy" :nodata="!chart" class="frame">
            <Chart type="AreaChart" :data="chart" :options="options" :unit="2" />
            <template #v-axis-unit>人</template>
            <template #h-axis-unit>日</template>
          </FrameTemplate>
          <div class="legends">
            <div class="item circle sales">実績（来店済み+予約受付）</div>
            <div class="item dailytarget">日別目標</div>
            <div class="item monthlytarget">月別目標</div>
          </div>
        </template>
      </template>
      <template #table>
        <ProgressTable />
      </template>
      <template #no-set>目標が未設定です<br />目標設定ページで設定してください</template>
    </AnalysisContent>
  </AnalysisPage>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { numberFormat } from '@/utils/number'
import { toOperator } from '@/utils/analysis'
import AnalysisPage from '@/components/templates/AnalysisPage'
import AnalysisContent from '@/components/molecules/AnalysisContent'
import AnalysisAggregate from '@/components/organisms/AnalysisAggregate'
import Chart, { FrameTemplate } from '@/components/molecules/Chart'
import LoadingIndicator from '@/components/molecules/LoadingIndicator'
import ProgressTable from './Components/ProgressTable'
import FluctuationIcon from './Components/FluctuationIcon'

export default {
  name: 'AnalysisProgress',
  components: {
    AnalysisPage,
    AnalysisContent,
    AnalysisAggregate,
    ProgressTable,
    Chart,
    FrameTemplate,
    FluctuationIcon,
    LoadingIndicator,
  },
  data() {
    return {
      isMounted: false,
    }
  },
  computed: {
    ...mapState({
      conditions: state => state.analysis.aggregate.conditions,
      busy: state => state.analysis.progress.busy,
      summary: state => state.analysis.progress.summary,
      table: state => state.analysis.progress.table,
    }),
    ...mapGetters({
      query: 'analysis/aggregate/query',
      chart: 'analysis/progress/chart',
      colors: 'analysis/colors/progress',
    }),
    achievementRate() {
      const [integer, decimal] = parseFloat(this.summary?.achievementRate ?? 0)
        .toFixed(1)
        .split('.')
      return {
        integer,
        decimal,
      }
    },
    diff() {
      if (this.summary === null) {
        return 0
      }
      return this.summary.achievement - this.summary.target
    },
    diffType() {
      switch (true) {
        case this.diff > 0:
          return 1
        case this.diff < 0:
          return 2
        default:
          return 0
      }
    },
    target() {
      return this.summary?.target ?? 0
    },
    achievement() {
      return this.summary?.achievement ?? 0
    },
    visited() {
      return this.summary?.visited ?? 0
    },
    mom() {
      return this.summary?.mom ?? { fluctuationType: 0, fluctuationValue: 0 }
    },
    yoy() {
      return this.summary?.yoy ?? { fluctuationType: 0, fluctuationValue: 0 }
    },
    options() {
      return {
        colors: this.colors,
        height: 315,
        annotations: {
          style: 'line',
          stem: {
            color: '#47A3CC',
          },
        },
        series: {
          0: { areaOpacity: 0.4 },
          1: { type: 'line', pointSize: 0 },
          2: { areaOpacity: 0.2, pointSize: 0 },
        },
        vAxis: {
          gridline: {
            count: 6,
          },
        },
        chartArea: {
          height: 274,
        },
      }
    },
  },
  watch: {
    $route() {
      this.fetch()
    },
  },
  async mounted() {
    await this.fetch()
    this.isMounted = true
  },
  methods: {
    numberFormat,
    toOperator,
    async fetch() {
      const { dispatch } = this.$store
      const { endTerm } = this.query
      await dispatch('analysis/progress/get', { endTerm })
    },
  },
}
</script>

<style scoped lang="postcss">
>>> .content > .graph {
  min-height: 624px;
}

.operator {
  display: inline-block;
  text-align: center;
  width: 1em;
}

.progress-info {
  display: flex;
  flex-direction: column;

  .achievement-text {
    background-color: var(--very-dark-gray);
    color: var(--white);
    font-size: 11px;
    text-align: center;
    width: 72px;
  }

  ul {
    align-items: center;
    display: flex;

    li + li {
      margin-left: 16px;
      padding-left: 16px;
      position: relative;

      &::before {
        border-left: 1px solid var(--border-color);
        bottom: 0;
        box-sizing: border-box;
        content: '';
        display: inline-block;
        height: 56px;
        left: 0;
        margin: auto 0;
        position: absolute;
        top: 0;
      }
    }

    .achievement {
      font-size: 56px;
      font-weight: 600;
      letter-spacing: -0.56px;
      .decimal {
        font-size: 28px;
        letter-spacing: 0;
      }
      .rate {
        font-size: 24px;
        margin-left: 2px;
      }
    }

    .diff {
      font-size: 11px;
      font-weight: 600;
      .operator {
        font-size: 14px;
      }
      .value {
        font-size: 18px;
      }
    }

    .compare {
      font-size: 12px;
      p {
        align-items: center;
        display: flex;

        .value {
          font-size: 13px;
          font-weight: 600;
        }

        >>> svg {
          height: 16px;
          margin-left: 4px;
          width: 16px;
        }
      }
    }

    .breakdown {
      p {
        font-size: 12px;
        .value {
          font-size: 13px;
          font-weight: 600;
          margin-left: 8px;
        }
      }
      .visited {
        align-items: baseline;
        display: flex;
        font-size: 10px;
        justify-content: space-between;

        &::before {
          color: var(--light-gray);
          content: '[';
          font-size: 12px;
        }
        &::after {
          color: var(--light-gray);
          content: ']';
          font-size: 12px;
        }
      }
    }
  }
}

hr {
  background-color: var(--border-color);
  border: none;
  height: 1px;
  margin-bottom: 24px;
}

.frame {
  height: 352px;
}

.legends {
  display: flex;
  font-size: 10px;
  justify-content: flex-end;
  margin-right: 16px;

  .item {
    line-height: 1;
    &::before {
      content: '';
      display: inline-block;
      height: 8px;
      margin-right: 8px;
      width: 8px;
    }

    &.circle::before {
      border-radius: 50%;
    }

    /** TODO 色整理 */
    &.sales::before {
      background-color: var(--light-blue-1);
    }
    &.dailytarget::before {
      background-color: var(--very-light-yellow);
    }
    &.monthlytarget::before {
      background-color: var(--moderate-orange);
    }
  }

  .item + .item {
    margin-left: 16px;
  }
}
</style>
